import React, { useState, useEffect, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Grid from '@mui/material/Grid';
import PropTypes from 'prop-types';
import Button from '@mui/material/Button';
import makeStyles from '@mui/styles/makeStyles';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import { STATISTICS_ENCOURAGEMENT_MESSAGE } from '../../constants/routes';
import {
  postStatisticsEncouragementMessage,
  putStatisticsEncouragementMessage,
} from '../../store/statisticsEncouragementMessage/action';
import { CIRCULAR_PROGRESS } from '../../constants/stylesDefault';
import { NotificationContext } from '../../contexts/NotificationContext';
import { useUserAuth } from '../../contexts/UserAuthContext.js';

const useStyles = makeStyles({
  form: {
    width: '100%',
  },
  gridContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  theme: {
    marginLeft: 30,
  },
  button: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: 30,
  },
  gridContainerLimit: {
    display: 'flex',
    alignItems: 'center',
    marginTop: 30,
  },
  loading: CIRCULAR_PROGRESS,
});

export default function StatisticsEncouragementMessageForm({
  statisticsEncouragementMessage,
}) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { token } = useUserAuth();

  const [id, setId] = useState('');
  const [title, setTitle] = useState('');
  const [titleError, setTitleError] = useState('');
  const [message, setMessage] = useState('');
  const [messageError, setMessageError] = useState('');
  const [minLimit, setMinLimit] = useState(0);
  const [minLimitError, setMinLimitError] = useState('');
  const [maxLimit, setMaxLimit] = useState(0);
  const [maxLimitError, setMaxLimitError] = useState('');
  const { notifyApiError } = useContext(NotificationContext);

  const { sending } = useSelector(
    (state) => state.statisticsEncouragementMessage,
  );

  const isEditing = id !== '';

  useEffect(() => {
    if (statisticsEncouragementMessage !== undefined && statisticsEncouragementMessage !== null) {
      setId(statisticsEncouragementMessage.id);
      setTitle(statisticsEncouragementMessage.title);
      setMessage(statisticsEncouragementMessage.message);
      setMinLimit(statisticsEncouragementMessage.min_limit);
      setMaxLimit(statisticsEncouragementMessage.max_limit);
    }
  }, [statisticsEncouragementMessage]);

  const onSubmit = (event) => {
    event.preventDefault();
    event.stopPropagation();
    let errorCount = 0;
    if (title.trim() === '') {
      setTitleError('Champ obligatoire');
      errorCount++;
    }
    if (message.trim() === '') {
      setMessageError('Champ obligatoire');
      errorCount++;
    }
    if (minLimit === null || minLimit === '') {
      setMinLimitError('Champ obligatoire');
      errorCount++;
    } else if (minLimit < 0) {
      setMinLimitError('Veuillez saisir une valeur supérieure ou égale à 0');
      errorCount++;
    }
    if (maxLimit === null || maxLimit === '') {
      setMaxLimitError('Champ obligatoire');
      errorCount++;
    } else if (maxLimit < 0) {
      setMaxLimitError('Veuillez saisir une valeur supérieure ou égale à 0');
      errorCount++;
    } else if (maxLimit <= minLimit) {
      setMaxLimitError('Veuillez saisir une valeur supérieure ou égale à la limite minimum');
      errorCount++;
    }
    if (errorCount === 0) {
      const data = {
        id,
        title,
        message,
        min_limit: minLimit,
        max_limit: maxLimit,
      };
      if (isEditing) {
        dispatch(putStatisticsEncouragementMessage(token, id, data))
          .then(() => navigate(STATISTICS_ENCOURAGEMENT_MESSAGE))
          .catch((error) => notifyApiError(error));
      } else {
        dispatch(postStatisticsEncouragementMessage(token, data))
          .then(() => navigate(STATISTICS_ENCOURAGEMENT_MESSAGE))
          .catch((error) => notifyApiError(error));
      }
    }
  };

  return (
    <form onSubmit={onSubmit} className={classes.form}>
      <Grid
        container
        direction="column"
        alignItems="center"
        spacing={2}
      >
        <Grid item>
          <TextField
            id="title"
            label="title"
            onChange={(e) => {
              setTitle(e.target.value);
              setTitleError('');
            }}
            value={title}
            multiline
            fullWidth
            error={titleError !== ''}
            helperText={titleError}
            variant="standard"
          />
        </Grid>
        <Grid item>
          <TextField
            id="message"
            label="Message"
            onChange={(e) => {
              setMessage(e.target.value);
              setMessageError('');
            }}
            value={message}
            multiline
            fullWidth
            error={messageError !== ''}
            helperText={messageError}
            variant="standard"
          />
        </Grid>
        <Grid item>
          <TextField
            id="minLimit"
            type="number"
            label="Limite minimum"
            onChange={(e) => {
              setMinLimit(parseInt(e.target.value, 10));
              setMinLimitError('');
            }}
            value={minLimit}
            error={minLimitError !== ''}
            helperText={minLimitError}
            variant="standard"
          />
        </Grid>
        <Grid item>
          <TextField
            id="maxLimit"
            type="number"
            label="Limite maximum"
            onChange={(e) => {
              setMaxLimit(parseInt(e.target.value, 10));
              setMaxLimitError('');
            }}
            value={maxLimit}
            error={maxLimitError !== ''}
            helperText={maxLimitError}
            variant="standard"
          />
        </Grid>
        <Grid item>
          <Button
            variant="contained"
            color="secondary"
            type="submit"
            disabled={sending}
          >
            {isEditing
              ? 'Editer'
              : 'Valider'}
            {sending && <CircularProgress size={24} />}
          </Button>
        </Grid>
      </Grid>
    </form>
  );
}

StatisticsEncouragementMessageForm.propTypes = {
  statisticsEncouragementMessage: PropTypes.object,
};

StatisticsEncouragementMessageForm.defaultProps = {
  statisticsEncouragementMessage: null,
};
